import type { ChartSeries, ChartValueFormat } from '@fruitful/types';
import {
    type ColorTokenKey,
    LabelRegular,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { getHorizontalBarColorToken } from './chartPalettes';
import {
    CHART_Y_SECTIONS,
    formatAxisValue,
    getAxisMax,
    toChartValue,
} from './chartUtils';

export const HorizontalBarChart = ({
    series,
    valueFormat,
    getColor,
}: {
    series: ChartSeries;
    valueFormat: ChartValueFormat;
    getColor: (token: ColorTokenKey) => string;
}) => {
    const points = [...series.points]
        .sort((a, b) => b.value - a.value)
        .slice(0, 10);
    const chartValues = points.map((point) =>
        toChartValue(point.value, valueFormat),
    );
    const maximumValue = Math.max(...chartValues, 0);
    const axisMaximum = getAxisMax(chartValues, CHART_Y_SECTIONS);
    const ticks = Array.from(
        { length: CHART_Y_SECTIONS + 1 },
        (_, index) => (axisMaximum / CHART_Y_SECTIONS) * index,
    );

    return (
        <YStack width="100%" gap="$3">
            {points.map((point, index) => {
                const value = chartValues[index] ?? 0;
                const width =
                    axisMaximum <= 0
                        ? 0
                        : Math.max(0, (value / axisMaximum) * 100);
                const color = getColor(
                    getHorizontalBarColorToken(value, maximumValue),
                );

                return (
                    <YStack
                        key={`${point.label}-${index.toString()}`}
                        width="100%"
                        gap="$1"
                    >
                        <LabelRegular color="$black" numberOfLines={1}>
                            {point.label}
                        </LabelRegular>
                        <View width="100%" height={24}>
                            <View
                                height={24}
                                width={`${width.toString()}%`}
                                borderRadius={8}
                                opacity={0.9}
                                style={{ backgroundColor: color }}
                            />
                        </View>
                    </YStack>
                );
            })}
            <XStack width="100%" justifyContent="space-between">
                {ticks.map((tick) => (
                    <LabelRegular
                        key={tick.toString()}
                        color="$gray1000"
                        textAlign="center"
                    >
                        {formatAxisValue(tick, valueFormat)}
                    </LabelRegular>
                ))}
            </XStack>
        </YStack>
    );
};
